import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Menu, X } from 'lucide-react';
import styles from './AdminLayout.module.scss';
import { useUserState } from '../../../providers/user-state.provider.ts';
import { ADMIN_NAV_SECTIONS } from '../../../types/admin.constants';
import { Routes } from '../../../types/constants';
import { isAdmin, isSuperAdmin } from '../../../utils/roles';
import { fetchPaymentOperations } from '../../../services/admin.service';

const AdminLayout = () => {
    const { isUserStateLoading, getUserInfo } = useUserState();
    const location = useLocation();
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const [isDesktopNavCollapsed, setIsDesktopNavCollapsed] = useState(() => (
        window.localStorage.getItem('combattv-admin-nav-collapsed') === 'true'
    ));
    const [paymentAttentionCount, setPaymentAttentionCount] = useState(0);
    const [isPaymentOperationsCheckUnavailable, setIsPaymentOperationsCheckUnavailable] = useState(false);
    const userInfo = getUserInfo();
    const isAdminUser = isAdmin(userInfo?.roles);
    const isSuperAdminUser = isSuperAdmin(userInfo?.roles);
    const visibleNavSections = useMemo(() => (
        ADMIN_NAV_SECTIONS
            .map((section) => ({
                ...section,
                items: section.items.filter((item) => !item.superAdminOnly || isSuperAdminUser)
            }))
            .filter((section) => section.items.length > 0)
    ), [isSuperAdminUser]);
    const currentNavItem = visibleNavSections
        .flatMap((section) => section.items)
        .find((item) => item.route && (
            location.pathname === item.route ||
            location.pathname.startsWith(`${item.route}/`)
        ));

    useEffect(() => {
        setIsMobileNavOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        window.localStorage.setItem('combattv-admin-nav-collapsed', String(isDesktopNavCollapsed));
    }, [isDesktopNavCollapsed]);

    useEffect(() => {
        let isCurrent = true;
        let requestVersion = 0;
        if (!isAdminUser || !userInfo?.token) {
            setPaymentAttentionCount(0);
            setIsPaymentOperationsCheckUnavailable(false);
            return undefined;
        }

        const refreshPaymentOperations = () => {
            const currentRequest = ++requestVersion;
            fetchPaymentOperations(userInfo.token)
                .then((summary) => {
                    if (isCurrent && currentRequest === requestVersion) {
                        setPaymentAttentionCount(summary.AttentionCount);
                        setIsPaymentOperationsCheckUnavailable(false);
                    }
                })
                .catch(() => {
                    if (isCurrent && currentRequest === requestVersion) {
                        setIsPaymentOperationsCheckUnavailable(true);
                    }
                });
        };

        refreshPaymentOperations();
        window.addEventListener('focus', refreshPaymentOperations);

        return () => {
            isCurrent = false;
            window.removeEventListener('focus', refreshPaymentOperations);
        };
    }, [isAdminUser, location.pathname, userInfo?.token]);

    useEffect(() => {
        if (!isMobileNavOpen) {
            return undefined;
        }

        const originalOverflow = document.body.style.overflow;
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMobileNavOpen(false);
            }
        };

        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = originalOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMobileNavOpen]);

    const renderAdminNav = (navClassName = styles.nav) => (
        <nav className={navClassName} aria-label="Admin navigation">
            {visibleNavSections.map((section) => (
                <div key={section.title ?? 'primary'} className={styles.section}>
                    {section.title ? <div className={styles.sectionTitle}>{section.title}</div> : null}
                    {section.items.map((item) => (
                        item.route ? (
                            <NavLink
                                key={item.name}
                                to={item.route}
                                end={item.route === Routes.ADMIN}
                                onClick={() => setIsMobileNavOpen(false)}
                                className={({ isActive }) =>
                                    isActive ? `${styles.navItem} ${styles.active}` : styles.navItem
                                }
                                aria-label={item.name}
                                title={isDesktopNavCollapsed ? item.name : undefined}
                            >
                                <span className={styles.compactLabel} aria-hidden="true">{item.name.slice(0, 3)}</span>
                                <span className={styles.navItemLabel}>{item.name}</span>
                            </NavLink>
                        ) : (
                            <div key={item.name} className={`${styles.navItem} ${styles.disabledItem}`}>
                                <span className={styles.compactLabel} aria-hidden="true">{item.name.slice(0, 3)}</span>
                                <span className={styles.navItemLabel}>{item.name}{item.isComingSoon ? ' (Coming Soon)' : ''}</span>
                            </div>
                        )
                    ))}
                </div>
            ))}
        </nav>
    );

    if (isUserStateLoading) {
        return <div className={styles.loading}>Loading admin workspace...</div>;
    }

    if (!isAdminUser) {
        return <Navigate to={Routes.HOME} replace />;
    }

    return (
        <div className={`${styles.page} ${isDesktopNavCollapsed ? styles.collapsed : ''}`}>
            <aside className={styles.sidebar} aria-label={isDesktopNavCollapsed ? 'Collapsed admin navigation' : 'Admin navigation'}>
                <div className={styles.sidebarHeader}>
                    <div className={styles.brand}>Admin</div>
                    <button
                        type="button"
                        className={styles.collapseButton}
                        onClick={() => setIsDesktopNavCollapsed((current) => !current)}
                        aria-label={isDesktopNavCollapsed ? 'Expand admin navigation' : 'Collapse admin navigation'}
                        aria-expanded={!isDesktopNavCollapsed}
                    >
                        {isDesktopNavCollapsed ? <ChevronRight aria-hidden="true" size={18} /> : <ChevronLeft aria-hidden="true" size={18} />}
                    </button>
                </div>
                {renderAdminNav()}
            </aside>
            <header className={styles.mobileHeader}>
                <div>
                    <div className={styles.mobileBrand}>Admin</div>
                    <div className={styles.mobileCurrentPage}>{currentNavItem?.name ?? 'Dashboard'}</div>
                </div>
                <button
                    type="button"
                    className={styles.mobileMenuButton}
                    onClick={() => setIsMobileNavOpen(true)}
                    aria-controls="admin-mobile-navigation"
                    aria-expanded={isMobileNavOpen}
                >
                    <Menu aria-hidden="true" size={20} />
                    <span>Menu</span>
                </button>
            </header>
            {isMobileNavOpen ? (
                <div className={styles.mobileNavLayer}>
                    <button
                        type="button"
                        className={styles.mobileBackdrop}
                        onClick={() => setIsMobileNavOpen(false)}
                        aria-label="Close admin navigation"
                    />
                    <aside
                        id="admin-mobile-navigation"
                        className={styles.mobileDrawer}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Admin navigation"
                    >
                        <div className={styles.mobileDrawerHeader}>
                            <div>
                                <div className={styles.mobileBrand}>Admin</div>
                                <div className={styles.mobileCurrentPage}>{currentNavItem?.name ?? 'Dashboard'}</div>
                            </div>
                            <button
                                type="button"
                                className={styles.mobileCloseButton}
                                onClick={() => setIsMobileNavOpen(false)}
                                aria-label="Close admin navigation"
                            >
                                <X aria-hidden="true" size={20} />
                            </button>
                        </div>
                        {renderAdminNav(styles.mobileNav)}
                    </aside>
                </div>
            ) : null}
            <main className={styles.main}>
                {paymentAttentionCount > 0 || isPaymentOperationsCheckUnavailable ? (
                    <div className={styles.paymentAlert} role="alert">
                        <span>
                            {paymentAttentionCount > 0
                                ? `${paymentAttentionCount} payment ${paymentAttentionCount === 1 ? 'operation requires' : 'operations require'} review.`
                                : 'Payment operations status is temporarily unavailable.'}
                        </span>
                        <NavLink to={Routes.ADMIN_PAYMENT_OPERATIONS}>Review payment operations</NavLink>
                    </div>
                ) : null}
                <Outlet />
            </main>
        </div>
    );
};

export default AdminLayout;
